import { Unzip, UnzipInflate } from "fflate";

const MAX_TRACK_BYTES = 100 * 1024 * 1024;

function isMp3(file: File) {
  return file.type === "audio/mpeg" || file.name.toLowerCase().endsWith(".mp3");
}

function isZip(file: File) {
  return file.type === "application/zip" || file.type === "application/x-zip-compressed" || file.name.toLowerCase().endsWith(".zip");
}

async function streamArchive(file: File, onFile: (file: File) => Promise<void>) {
  const reader = file.stream().getReader();
  let processing = Promise.resolve();
  let archiveError: Error | undefined;
  let count = 0;
  const unzip = new Unzip((entry) => {
    if (!entry.name.toLowerCase().endsWith(".mp3") || entry.name.endsWith("/")) return;
    const chunks: Uint8Array<ArrayBuffer>[] = [];
    let size = 0;
    entry.ondata = (error, chunk, final) => {
      if (error) { archiveError = new Error(`${file.name}: The ZIP archive could not be read.`); return; }
      size += chunk.byteLength;
      if (size > MAX_TRACK_BYTES) {
        archiveError = new Error(`${entry.name}: MP3 files are limited to 100 MB.`);
        entry.terminate();
        return;
      }
      if (chunk.byteLength) {
        const copy = new Uint8Array(chunk.byteLength);
        copy.set(chunk);
        chunks.push(copy);
      }
      if (!final) return;
      const name = entry.name.split("/").pop()?.trim() || "track.mp3";
      const extracted = new File(chunks, name, { type: "audio/mpeg", lastModified: file.lastModified });
      count += 1;
      processing = processing.then(() => onFile(extracted));
    };
    entry.start();
  });
  unzip.register(UnzipInflate);

  try {
    while (true) {
      const { value, done } = await reader.read();
      unzip.push(value ?? new Uint8Array(), done);
      await processing;
      if (archiveError) throw archiveError;
      if (done) break;
    }
    await processing;
    if (archiveError) throw archiveError;
    return count;
  } catch (error) {
    await reader.cancel().catch(() => undefined);
    if (error instanceof Error && error.message.includes("MP3 files are limited")) throw error;
    throw new Error(`${file.name}: The ZIP archive could not be read.`);
  }
}

export async function visitMusicFiles(files: File[], onFile: (file: File) => Promise<void>) {
  let count = 0;
  for (const file of files) {
    if (isMp3(file)) {
      count += 1;
      await onFile(file);
    } else if (isZip(file)) {
      count += await streamArchive(file, onFile);
    }
  }
  return count;
}
